import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Search } from "lucide-react";
import { useCmsAuth } from "../context/CmsAuthContext";
import {
  DOCS_BY_ID,
  DOCS_CATALOG,
  DOCS_ROUTE,
  DOCS_SECTION_DEFAULTS,
  DOCS_SECTION_LABELS,
  buildDocsTree,
  canAccessDocsSection,
  docsSectionPath,
  filterDocsCatalog,
  isDocsSectionId,
  libraryItemsForUser,
} from "../lib/docsCatalog";
import { renderDocsHtml } from "../lib/docsMarkdown";

function cn(...parts) {
  return parts.filter(Boolean).join(" ");
}

function ArticleLink({ article, active, onNavigate }) {
  return (
    <Link
      to={docsSectionPath(article.section, article.id)}
      onClick={onNavigate}
      className={cn(
        "block rounded-lg px-2.5 py-1.5 text-[13px] leading-snug transition-colors text-brand-dark",
        active ? "bg-brand-soft text-brand-primary font-semibold" : "hover:bg-brand-soft/70"
      )}
    >
      {article.title}
    </Link>
  );
}

export default function DocsLibraryPage() {
  const { profile } = useCmsAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { seccion } = useParams();
  const [params] = useSearchParams();
  const [query, setQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const section = isDocsSectionId(seccion) ? seccion : null;
  const requestedId = params.get("doc");
  const requestedArticle = requestedId ? DOCS_BY_ID[requestedId] : undefined;

  const docId =
    requestedArticle && section && requestedArticle.section === section
      ? requestedArticle.id
      : section
        ? DOCS_SECTION_DEFAULTS[section]
        : null;
  const article = docId ? DOCS_BY_ID[docId] : undefined;
  const html = useMemo(() => (article ? renderDocsHtml(article.source, article.id) : ""), [article]);

  const sectionArticles = useMemo(
    () => (section ? DOCS_CATALOG.filter((item) => item.section === section) : []),
    [section]
  );
  const filtered = useMemo(() => filterDocsCatalog(query, sectionArticles), [query, sectionArticles]);
  const tree = useMemo(() => buildDocsTree(filtered), [filtered]);
  const navSections = libraryItemsForUser(profile);

  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(decodeURIComponent(hash))?.scrollIntoView({ block: "start" });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [docId, html, location.hash]);

  if (!section) {
    return <Navigate to={docsSectionPath("usuario")} replace />;
  }

  if (!canAccessDocsSection(profile, section)) {
    return <Navigate to={docsSectionPath("usuario")} replace />;
  }

  if (requestedArticle && !canAccessDocsSection(profile, requestedArticle.section)) {
    return <Navigate to={docsSectionPath("usuario")} replace />;
  }

  if (requestedArticle && requestedArticle.section !== section) {
    return (
      <Navigate to={docsSectionPath(requestedArticle.section, requestedArticle.id)} replace />
    );
  }

  function handleArticleClick(event) {
    const anchor = event.target.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href) return;
    if (/^(https?:|mailto:|tel:)/i.test(href)) return;

    if (href.startsWith("#")) {
      event.preventDefault();
      document.getElementById(decodeURIComponent(href.slice(1)))?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch {
      return;
    }
    if (url.pathname.startsWith(DOCS_ROUTE) || href.startsWith("?doc=")) {
      event.preventDefault();
      const docParam = url.searchParams.get("doc");
      if (docParam && DOCS_BY_ID[docParam]) {
        const target = DOCS_BY_ID[docParam];
        if (!canAccessDocsSection(profile, target.section)) {
          navigate(docsSectionPath("usuario"));
          return;
        }
        navigate({
          pathname: `${DOCS_ROUTE}/${target.section}`,
          search: `?doc=${encodeURIComponent(target.id)}`,
          hash: url.hash,
        });
        return;
      }
      navigate({
        pathname: url.pathname.startsWith(DOCS_ROUTE) ? url.pathname : location.pathname,
        search: url.search,
        hash: url.hash,
      });
    }
  }

  const sectionLabel = DOCS_SECTION_LABELS[section];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-wrap gap-2 mb-6">
        {navSections.map((item) => (
          <Link
            key={item.id}
            to={docsSectionPath(item.id)}
            className={cn(
              "px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide border transition-colors",
              item.id === section
                ? "bg-brand-primary text-white border-brand-primary"
                : "bg-white text-brand-dark/70 border-brand-soft hover:border-brand-primary/40"
            )}
          >
            {item.label}
          </Link>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start gap-0 rounded-2xl border border-brand-soft bg-white overflow-hidden shadow-sm">
        <aside className="lg:w-80 xl:w-[22rem] shrink-0 border-b lg:border-b-0 lg:border-r border-brand-soft bg-[#fafcfb] lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <div className="px-4 pt-5 pb-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45 mb-2">
              Tutorial · {sectionLabel}
            </p>
            <label className="relative block">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-dark/40"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Buscar en ${sectionLabel.toLowerCase()}…`}
                className="w-full rounded-lg border border-brand-soft bg-white pl-8 pr-3 py-2 text-sm text-brand-dark placeholder:text-brand-dark/40 focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              />
            </label>
            <button
              type="button"
              className="lg:hidden mt-3 text-xs font-bold uppercase tracking-wide text-brand-primary"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              {mobileNavOpen ? "Ocultar índice" : "Mostrar índice"}
            </button>
          </div>

          <nav
            className={cn("px-3 pb-6 space-y-4", mobileNavOpen ? "block" : "hidden lg:block")}
            aria-label={`Índice ${sectionLabel}`}
          >
            {tree.map((treeSection) => (
              <div key={treeSection.id}>
                {treeSection.groups.map((group) => (
                  <div key={`${treeSection.id}-${group.id || "root"}`} className="mb-2">
                    {group.id || treeSection.groups.length > 1 ? (
                      <p className="px-2.5 pt-1.5 pb-1 text-[11.5px] font-bold uppercase tracking-wide text-brand-dark">
                        {group.label}
                      </p>
                    ) : null}
                    <ul>
                      {group.articles.map((item) => (
                        <li key={item.id}>
                          <ArticleLink
                            article={item}
                            active={item.id === docId}
                            onNavigate={() => setMobileNavOpen(false)}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="px-2.5 text-xs text-brand-dark/50">
                Ningún documento coincide con «{query}».
              </p>
            )}
            <p className="px-2.5 pt-2 text-[10px] text-brand-dark/35">
              {sectionArticles.length} documentos en esta sección.
            </p>
          </nav>
        </aside>

        <div className="flex-1 min-w-0 px-4 sm:px-8 py-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-sm text-brand-primary hover:underline mb-6"
          >
            <ArrowLeft size={16} />
            Volver al panel
          </Link>

          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-brand-soft text-[10px] font-bold uppercase tracking-widest text-brand-primary mb-4">
            AGDYSA · Tutorial
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center">
              <BookOpen size={20} className="text-brand-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-extrabold text-brand-dark">{sectionLabel}</h1>
              <p className="text-sm text-brand-dark/50">
                {section === "usuario"
                  ? "Procedimientos de operación por módulo y rol"
                  : section === "tecnico"
                    ? "Contratos, arquitectura y operación técnica"
                    : "Concepción, funcionamiento e historia del producto"}
              </p>
            </div>
          </div>

          {!article ? (
            <div className="mt-10 max-w-xl rounded-xl border border-brand-soft bg-[#fafcfb] p-6">
              <p className="text-sm font-semibold text-brand-dark">Documento no disponible</p>
              <p className="mt-2 text-sm text-brand-dark/60">
                <code className="text-xs">{requestedId ?? docId}</code> no forma parte de esta sección
                o no está empaquetado.
              </p>
              <Link
                to={docsSectionPath(section)}
                className="inline-block mt-4 text-sm font-semibold text-brand-primary hover:underline"
              >
                Ir al índice
              </Link>
            </div>
          ) : (
            <article className="mt-8">
              <p className="text-[11px] font-mono text-brand-dark/35 mb-4 break-all">docs/{article.id}</p>
              <div
                className="docs-prose"
                onClick={handleArticleClick}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
